import React, { useState, useEffect, useRef } from 'react'
import { Modal, Spin } from 'antd'
import { createEditData, isDataModified, createEditDataFromDetailData } from './EditData'
import { useTranslation } from 'react-i18next'
import CustomLabelInput from './CustomLabelInput'
import Profile from './Profile'
import styles from './style.module.scss'
import Emails from './Emails'
import Phones from './Phones'
import URLs from './URLs'
import Relations from './Relations'
import Addresses from './Addresses'
import Dates from './Dates'
import Customs from './Customs'
import Memos from './Memos'
import Category from './Category'
import CategorySelector from 'pages/contacts/components/SharedContacts/CategorySelector'
import dayjs from 'dayjs'
import { doSave } from './EditProcess'
import { Loading3QuartersOutlined } from '@ant-design/icons'

interface SharedContactEditorProps {
  onClose: (shouldRefresh: boolean) => void
  initialContact?: any
  profileColor?: string
  initialDetailData?: any
  initCategory?: any
}

function SharedContactEditor({
  onClose,
  initialContact,
  profileColor = '#ac725e',
  initialDetailData = null,
  initCategory = null,
}: SharedContactEditorProps) {
  const { t } = useTranslation()

  const isDuplicate = initialDetailData?.duplicate || initialContact?.duplicate
  if (initialDetailData?.duplicate) {
    delete initialDetailData.duplicate
    delete initialDetailData.id
    delete initialDetailData.etag
  }
  if (initialContact?.duplicate) {
    delete initialContact.duplicate
    delete initialContact.id
    delete initialContact.etag
  }

  const [editData, setEditData] = useState(
    initialDetailData
      ? createEditDataFromDetailData(initialDetailData)
      : createEditData(initialContact),
  )
  const [isPersonal, _] = useState(initialDetailData?.isPersonal || false)
  const [customLabelModalCallback, setCustomLabelModalCallback] = useState<
    ((label: string) => void) | null
  >(null)
  const [isCategorySelectorOpen, setIsCategorySelectorOpen] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [showLoading, setShowLoading] = useState(false)

  const orgEditData = useRef<any>(null)
  useEffect(() => {
    if (isDuplicate) {
      orgEditData.current = createEditData(null)
      return
    }

    if (editData.id == null && initCategory != null && Object.keys(initCategory).length > 0) {
      setEditData({ ...editData, categories: [initCategory] })
    }

    orgEditData.current = { ...editData }
  }, [])

  useEffect(() => {
    setHasChanges(isDataModified(orgEditData.current, editData))
  }, [editData])

  const handleFieldChange = (field: string, value: any) => {
    setEditData({
      ...editData,
      [field]: value,
    })
  }

  const handleAddSection = (propName: string, label: string) => {
    if (label === 'custom') {
      setCustomLabelModalCallback(() => (label: string) => handleAddSection(propName, label))
      return
    }

    if (propName.startsWith('addresses')) {
      setEditData({
        ...editData,
        addresses: [
          ...editData.addresses,
          { type: label, street: '', city: '', state: '', postalCode: '', country: '', po_box: '' },
        ],
      })
      return
    }

    if (propName === 'dates') {
      setEditData({
        ...editData,
        dates: [...editData.dates, { type: label, value: dayjs().format('YYYY-MM-DD') }],
      })
      return
    }

    setEditData({
      ...editData,
      [propName]: [...editData[propName], { type: label, value: '' }],
    })
  }

  const handleSectionDelete = (propName: string, index: number) => {
    if (propName.startsWith('addresses')) {
      setEditData({
        ...editData,
        addresses: editData.addresses.filter((_: any, i: number) => i !== index),
      })
      return
    } else if (propName === 'notes') {
      const willDeleteItem = editData.notes[index]
      if (willDeleteItem.id) {
        const oldDeletedNotes = editData.deleteNotes || []
        setEditData({
          ...editData,
          notes: editData.notes.filter((_: any, i: number) => i !== index),
          deleteNotes: [...oldDeletedNotes, willDeleteItem],
        })
        return
      }
    }

    setEditData({
      ...editData,
      [propName]: editData[propName].filter((_: any, i: number) => i !== index),
    })
  }

  const handleSectionValueChange = (propName: string, index: number, value: any) => {
    if (propName.startsWith('addresses')) {
      const name = propName.split('/')[1]

      setEditData({
        ...editData,
        addresses: editData.addresses.map((address: any, i: number) =>
          i === index ? { ...address, [name]: value } : address,
        ),
      })
      return
    }

    const newSections = []
    for (let i = 0; i < editData[propName].length; i++) {
      if (i === index) {
        newSections.push({ ...editData[propName][i], value })
      } else {
        newSections.push(editData[propName][i])
      }
    }
    setEditData({
      ...editData,
      [propName]: newSections,
    })
  }

  const handleSectionLabelChange = (propName: string, index: number, label: string) => {
    if (label === 'custom') {
      setCustomLabelModalCallback(
        () => (label: string) => handleSectionLabelChange(propName, index, label),
      )
      return
    }

    if (propName.startsWith('addresses')) {
      setEditData({
        ...editData,
        addresses: editData.addresses.map((address: any, i: number) =>
          i === index ? { ...address, type: label } : address,
        ),
      })
      return
    }

    const newSections = []
    for (let i = 0; i < editData[propName].length; i++) {
      if (i === index) {
        newSections.push({ ...editData[propName][i], type: label })
      } else {
        newSections.push(editData[propName][i])
      }
    }

    setEditData({
      ...editData,
      [propName]: newSections,
    })
  }

  const handleImageChange = (file: File | null) => {
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          if (img.width > 150 || img.height > 150) {
            const canvas = document.createElement('canvas')
            canvas.width = 150
            canvas.height = 150

            const ctx = canvas.getContext('2d')
            if (!ctx) return

            const scale = Math.min(150 / img.width, 150 / img.height)
            const newWidth = img.width * scale
            const newHeight = img.height * scale

            const x = (150 - newWidth) / 2
            const y = (150 - newHeight) / 2

            ctx.fillStyle = 'transparent'
            ctx.fillRect(0, 0, 150, 150)
            ctx.drawImage(img, x, y, newWidth, newHeight)

            const resizedDataUrl = canvas.toDataURL('image/png')

            canvas.toBlob(async (blob) => {
              if (!blob) return
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              })
              setEditData({
                ...editData,
                photo: { url: resizedDataUrl, file: resizedFile },
              })
            }, file.type)
          } else {
            setEditData({
              ...editData,
              photo: { url: e.target!.result as string, file: file },
            })
          }
        }
        img.src = e.target!.result as string
      }
      reader.readAsDataURL(file)
    } else {
      setEditData({
        ...editData,
        photo: null,
      })
    }
  }

  const handleCategoryDelete = (index: number) => {
    const newCategories = [...editData.categories]
    newCategories.splice(index, 1)

    setEditData({
      ...editData,
      categories: newCategories,
    })
  }

  const handleAddCategory = () => {
    console.log('handleAddCategory')
    setIsCategorySelectorOpen(true)
  }

  const handleCategorySave = (selectedCategories: any[]) => {
    setEditData({
      ...editData,
      categories: [...selectedCategories],
    })
    setIsCategorySelectorOpen(false)
  }

  const handleDeletedCategory = (category: any) => {
    // Category 존재가 제거되는 경우 호출
    const newCategories = editData.categories.filter((c) => c.id !== category.id)
    setEditData({
      ...editData,
      categories: newCategories,
    })
  }

  const modalFooter = [
    <div key="footer" className={styles.footer}>
      <button className={styles.cancelButton} onClick={() => onClose(false)}>
        {t('common.button.cancel')}
      </button>
      <button
        className={`${styles.saveButton} ${!hasChanges ? styles.disabled : ''}`}
        onClick={() => handleSave()}
        disabled={!hasChanges}
      >
        {t('common.button.save')}
      </button>
    </div>,
  ]

  const handleSave = () => {
    if (showLoading) {
      return
    }

    doSave(
      editData,
      orgEditData.current,
      false,
      (isProcessing) => {
        setShowLoading(isProcessing)
      },
      (success) => {
        if (success) {
          onClose(!editData.isPersonal && !editData.id)
        }
      },
      () => {
        setShowLoading(false)
      },
    )
  }

  const modalTitle = initialContact?.id
    ? t('contact.title.contactEdit')
    : isPersonal
      ? t('contact.toolbar.newContact')
      : t('contact.toolbar.newSharedContact')

  return (
    <>
      <Modal
        title={`${modalTitle}`}
        open={true}
        closable={false}
        footer={modalFooter}
        width={600}
        className={styles.modal}
        centered
      >
        <div className={styles.formContainer}>
          <Spin indicator={<Loading3QuartersOutlined spin />} spinning={showLoading}>
            <Profile
              editData={editData}
              profileColor={profileColor}
              onFieldChange={handleFieldChange}
              onImageChange={handleImageChange}
            />
            <Emails
              editData={editData}
              onFieldChange={handleSectionValueChange}
              onLabelChange={handleSectionLabelChange}
              onAddItem={handleAddSection}
              onDelete={handleSectionDelete}
            />
            <Phones
              editData={editData}
              onFieldChange={handleSectionValueChange}
              onLabelChange={handleSectionLabelChange}
              onAddItem={handleAddSection}
              onDelete={handleSectionDelete}
            />
            <Addresses
              editData={editData}
              onFieldChange={handleSectionValueChange}
              onLabelChange={handleSectionLabelChange}
              onAddItem={handleAddSection}
              onDelete={handleSectionDelete}
            />
            <URLs
              editData={editData}
              onFieldChange={handleSectionValueChange}
              onLabelChange={handleSectionLabelChange}
              onAddItem={handleAddSection}
              onDelete={handleSectionDelete}
            />
            <Dates
              editData={editData}
              onFieldChange={handleSectionValueChange}
              onLabelChange={handleSectionLabelChange}
              onAddItem={handleAddSection}
              onDelete={handleSectionDelete}
            />
            <Relations
              editData={editData}
              onFieldChange={handleSectionValueChange}
              onLabelChange={handleSectionLabelChange}
              onAddItem={handleAddSection}
              onDelete={handleSectionDelete}
            />
            {!isPersonal && (
              <>
                <Customs
                  editData={editData}
                  onFieldChange={handleSectionValueChange}
                  onLabelChange={handleSectionLabelChange}
                  onAddItem={handleAddSection}
                  onDelete={handleSectionDelete}
                />
                <Category
                  editData={editData}
                  onDelete={handleCategoryDelete}
                  onAddItem={handleAddCategory}
                />
              </>
            )}
            <Memos
              editData={editData}
              onFieldChange={handleSectionValueChange}
              onLabelChange={handleSectionLabelChange}
              onAddItem={handleAddSection}
              onDelete={handleSectionDelete}
            />
          </Spin>
        </div>
      </Modal>
      {!!customLabelModalCallback && (
        <CustomLabelInput
          isOpen={true}
          onClose={() => {
            setCustomLabelModalCallback(null)
          }}
          onConfirm={(label) => {
            setCustomLabelModalCallback(null)
            customLabelModalCallback(label)
          }}
        />
      )}
      {isCategorySelectorOpen && (
        <CategorySelector
          initialData={editData.categories as any}
          onClose={() => setIsCategorySelectorOpen(false)}
          onSave={handleCategorySave}
          onDeletedCategory={handleDeletedCategory}
        />
      )}
    </>
  )
}

export default SharedContactEditor
